import { useMutation } from "@apollo/client/react";
import {
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DotsThreeOutlineVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsThreeOutlineVertical";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { EditableTextField, IfPermission } from "../../../components";
import Permissions from "../../../constants/permissions";
import {
  Auth0Identity,
  SaveUserMutation,
  SaveUserMutationVariables,
  SetAuth0UserBlockMutation,
  SetAuth0UserBlockMutationVariables,
  ResetAuth0PasswordMutation,
  ResetAuth0PasswordMutationVariables,
  ResetAuth0PasswordDocument,
  SaveUserDocument,
  SetAuth0UserBlockDocument,
} from "../../../graphql/types";

import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import { ClockIcon } from "@phosphor-icons/react/dist/csr/Clock";
import { PencilIcon } from "@phosphor-icons/react/dist/csr/Pencil";
import { baseAdminContainerStyles } from "../../../components/ui/common";
import { ChangePassword } from "./ChangePassword";

const IdentityCard = styled(Card)(({ theme }) => ({
  ...baseAdminContainerStyles(theme),
}));

const IdentityContent = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: "16px",
});

const CardName = ({
  name,
  email,
  connectionType,
  auth0Id,
}: {
  name: string;
  email: string;
  auth0Id: string;
  connectionType: string;
}) => {
  const [isEditing, setIsEditing] = useState({
    userName: false,
  });
  const userNameRef = useRef<HTMLInputElement>(null);
  const [saveUser, saveUserState] = useMutation<SaveUserMutation, SaveUserMutationVariables>(SaveUserDocument, {});
  const { control, getValues, setError, resetField } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      userName: name,
    },
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const newValue = getValues("userName").trim();
    if (saveUserState.loading || (newValue === name && e.key != "Escape")) return;
    switch (e.key) {
      case "Enter":
        e.preventDefault();
        if (!newValue) {
          setError("userName", { message: "Required field" });
          return;
        }
        saveUser({
          variables: {
            email,
            userName: newValue,
          },
          update: (cache, { data }) => {
            if (data?.saveUserV2.__typename !== "SaveUserSuccessMutationResult") return;
            cache.modify({
              id: cache.identify({
                __typename: "Auth0Identity",
                id: auth0Id,
              }),
              fields: {
                name: () => {
                  return newValue;
                },
              },
            });
          },
          onCompleted: (result: SaveUserMutation) => {
            if (result.saveUserV2.__typename === "SaveUserSuccessMutationResult") {
              toast.success("User's name saved");
              setIsEditing({
                userName: false,
              });
            }
          },
          onError: () => {
            toast.error(`Error when saving user's ${isEditing.userName ? "name" : "email"}`);
          },
        });

        break;
      case "Escape":
        if (isEditing.userName) {
          resetField("userName", {
            defaultValue: name,
          });
          setIsEditing({ userName: false });
        }

        break;
      default:
        break;
    }
  };
  useEffect(() => {
    if (userNameRef.current && isEditing.userName) userNameRef.current.focus();
  }, [userNameRef, isEditing.userName]);

  if (connectionType === "google-oauth2") {
    return (
      <Typography variant="heading.five" data-testid="user-name-label">
        {name}
      </Typography>
    );
  }

  return (
    <>
      {isEditing.userName ? (
        <EditableTextField
          name="userName"
          control={control}
          rules={{ required: true }}
          onKeyDown={handleKeyDown}
          inputRef={userNameRef}
          onBlur={() => {
            resetField("userName", {
              defaultValue: name,
            });
            setIsEditing({ ...isEditing, userName: false });
          }}
          isLoading={saveUserState.loading}
          onFocus={(e) => e.currentTarget.select()}
        />
      ) : (
        <Grid container sx={{ alignItems: "center" }}>
          <Typography variant="heading.five" data-testid="user-name-label">
            {name}
          </Typography>
          <IfPermission permission={Permissions.ManageUsers}>
            <IconButton
              aria-label={`edit name ${name}`}
              onClick={() => {
                setIsEditing({
                  userName: true,
                });
              }}
            >
              <PencilIcon />
            </IconButton>
          </IfPermission>
        </Grid>
      )}
    </>
  );
};

const Actions = ({
  connectionType,
  handleChangePassword,
  handleSendEmail,
  auth0Id,
  isCurrentlyBlocked,
}: {
  connectionType: string;
  handleChangePassword: () => void;
  handleSendEmail: () => void;
  auth0Id: string;
  isCurrentlyBlocked: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [menuOpened, setMenuOpened] = useState(false);
  const [setAuth0UserBlock] = useMutation<SetAuth0UserBlockMutation, SetAuth0UserBlockMutationVariables>(
    SetAuth0UserBlockDocument,
    {}
  );
  const idPrefix = connectionType === "Username-Password-Authentication" ? "auth0" : "google-oauth2";
  const handleBlockUser = () => {
    setAuth0UserBlock({
      variables: {
        id: `${idPrefix}|${auth0Id}`,
        blocked: !isCurrentlyBlocked,
      },
      update: (cache, { data }) => {
        if (!data?.setAuth0UserBlock?.ok) return;
        cache.modify({
          id: cache.identify({
            __typename: "Auth0Identity",
            id: auth0Id,
          }),
          fields: {
            blocked: (currentData) => {
              return !currentData;
            },
          },
        });
      },
      onCompleted: () => {
        toast.success(`User ${isCurrentlyBlocked ? "unblocked" : "blocked"}`);
      },
    });
  };

  return (
    <>
      <IconButton
        data-testid="menu-icon"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          setMenuOpened(true);
        }}
      >
        <DotsThreeOutlineVerticalIcon size={20} />
      </IconButton>
      <Menu anchorEl={anchorEl} onClose={() => setMenuOpened(false)} open={menuOpened}>
        {connectionType === "Username-Password-Authentication" && (
          <>
            <MenuItem
              onClick={() => {
                handleSendEmail();
                setMenuOpened(false);
              }}
            >
              Send password reset email (recommended)
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleChangePassword();
                setMenuOpened(false);
              }}
            >
              Change password (not recommended)
            </MenuItem>
          </>
        )}
        <MenuItem
          onClick={() => {
            setMenuOpened(false);
            handleBlockUser();
          }}
        >
          {isCurrentlyBlocked ? "Unblock in Auth0" : "Block in Auth0"}
        </MenuItem>
      </Menu>
    </>
  );
};

const Identities = ({ identities, email }: { identities: Auth0Identity[]; email: string }) => {
  const theme = useTheme();
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [resetPassword] = useMutation<ResetAuth0PasswordMutation, ResetAuth0PasswordMutationVariables>(
    ResetAuth0PasswordDocument,
    {
      onCompleted: () => {
        toast.success("Password reset email sent");
      },
      onError: () => {
        toast.error("Error sending password reset email");
      },
    }
  );

  const handleChangePassword = () => {
    setShowChangePasswordModal(true);
  };
  const handleSendEmail = () => {
    resetPassword({
      variables: {
        email,
      },
    });
  };

  return (
    <>
      {identities.length === 0 ? (
        <Typography variant="body.medium.regular" sx={{ display: "flex", justifyContent: "center", marginTop: "60px" }}>
          No identities found.
        </Typography>
      ) : (
        identities.map((identity) => (
          <IdentityCard key={identity.email + identity.connectionType}>
            <CardHeader
              sx={{ padding: "0" }}
              title={
                <Stack>
                  {identity.blocked ? <Chip label="Blocked" variant="error" /> : null}
                  <CardName
                    name={identity.name}
                    email={identity.email}
                    connectionType={identity.connectionType}
                    auth0Id={identity.id}
                  />
                </Stack>
              }
              subheader={identity.email}
              action={
                identity.connectionType === "Username-Password-Authentication" ? (
                  <Actions
                    connectionType={identity.connectionType}
                    handleChangePassword={handleChangePassword}
                    handleSendEmail={handleSendEmail}
                    auth0Id={identity.id}
                    isCurrentlyBlocked={identity.blocked}
                  />
                ) : null
              }
            />
            <CardContent sx={{ padding: "0", "&:last-child": { paddingBottom: "0" } }}>
              <IdentityContent>
                <Typography>
                  <strong>Connection Type:</strong> {identity.connectionType}
                </Typography>
                {identity.connectionType === "Username-Password-Authentication" ? (
                  <Stack spacing={1} direction="row" sx={{ alignItems: "center" }}>
                    <strong>Email Verified:</strong>{" "}
                    {identity.emailVerified ? (
                      <CheckCircleIcon size={20} color={theme.palette.success.main} />
                    ) : (
                      <Tooltip title="pending">
                        <ClockIcon size={20} />
                      </Tooltip>
                    )}
                  </Stack>
                ) : null}
              </IdentityContent>
            </CardContent>
          </IdentityCard>
        ))
      )}
      {showChangePasswordModal ? (
        <ChangePassword closeModal={() => setShowChangePasswordModal(false)} userEmail={email} />
      ) : null}
    </>
  );
};

export { Identities };
